import * as React from 'react';
import type { GuestVoucherOverrideDto } from './GuestVouchersModal';

interface BadgeProps {
    override: GuestVoucherOverrideDto | null;
    onClick: () => void;
}

/** Small badge button shown in the person header — clicking opens the modal */
export const OverrideBadge: React.FC<BadgeProps> = ({ override, onClick }) => (
    <button className="gv-override-badge" onClick={onClick} title="Edit voucher rate per week">
        <i className="fa-solid fa-sliders me-1"></i>
        {override ? `${override.redPerWeek}R / ${override.bluePerWeek}B per wk` : '1R / 1B per wk'}
    </button>
);

interface ModalProps {
    reservationId: string;
    personId: string;
    personName: string;
    override: GuestVoucherOverrideDto | null;
    onSaved: () => void;
    onClose: () => void;
}

export const OverrideModal: React.FC<ModalProps> = ({ reservationId, personId, personName, override, onSaved, onClose }) => {
    const [red, setRed] = React.useState(override?.redPerWeek ?? 1);
    const [blue, setBlue] = React.useState(override?.bluePerWeek ?? 1);
    const [notes, setNotes] = React.useState(override?.notes ?? '');
    const [saving, setSaving] = React.useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const r = await fetch(`/api/sales/guest-vouchers/overrides/${reservationId}/${personId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ redPerWeek: red, bluePerWeek: blue, notes: notes || null }),
            });
            if (!r.ok) { const j = await r.json(); alert(`Failed: ${j.error || 'Unknown'}`); }
            else { onSaved(); onClose(); }
        } catch { /* ignore */ }
        finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm('Remove override and revert to default (1 Red / 1 Blue per week)?')) return;
        const r = await fetch(`/api/sales/guest-vouchers/overrides/${reservationId}/${personId}`, { method: 'DELETE' });
        if (r.ok) { onSaved(); onClose(); }
    };

    const isDefault = red === 1 && blue === 1;

    return (
        <div className="gv-submodal-backdrop" onClick={onClose}>
            <div className="gv-submodal gv-submodal-sm" onClick={e => e.stopPropagation()}>
                <div className="gv-submodal-header">
                    <h3>Weekly Voucher Rate</h3>
                    <button className="gv-modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="gv-submodal-body">
                    <div className="gv-submodal-guest">
                        <i className="fa-solid fa-user"></i>
                        {personName}
                    </div>

                    <p className="gv-override-desc">
                        Set how many vouchers this guest earns per 7-day program window. Default is 1 Red + 1 Blue.
                    </p>

                    <div className="gv-override-fields">
                        <div className="gv-override-field">
                            <label className="gv-form-label">
                                <span className="gv-override-dot" style={{ backgroundColor: '#ef4444' }}></span>
                                Red per week
                            </label>
                            <input type="number" min="0" max="20" value={red} onChange={e => setRed(parseInt(e.target.value) || 0)}
                                className="form-control form-control-sm gv-override-num" />
                        </div>
                        <div className="gv-override-field">
                            <label className="gv-form-label">
                                <span className="gv-override-dot" style={{ backgroundColor: '#3b82f6' }}></span>
                                Blue per week
                            </label>
                            <input type="number" min="0" max="20" value={blue} onChange={e => setBlue(parseInt(e.target.value) || 0)}
                                className="form-control form-control-sm gv-override-num" />
                        </div>
                    </div>

                    <div className="gv-form-section">
                        <label className="gv-form-label">Notes</label>
                        <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                            className="form-control form-control-sm" placeholder="Reason for override..." />
                    </div>
                </div>

                <div className="gv-submodal-footer">
                    {override && (
                        <button className="btn btn-sm btn-outline-danger me-auto" onClick={remove} title="Remove override and revert to 1R/1B default">
                            <i className="fa-solid fa-rotate-left me-1"></i>Reset to Default
                        </button>
                    )}
                    <button className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn btn-sm btn-success" onClick={save} disabled={saving || (isDefault && !override)}>
                        {saving ? (
                            <><span className="spinner-border spinner-border-sm me-1" />Saving...</>
                        ) : (
                            <><i className="fa-solid fa-check me-1"></i>Save</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
